import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled, { CSSProperties } from 'styled-components';

import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';

import {
  SplitProgressBar_cardCollection,
  SplitProgressBar_userCardCollection,
} from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  width: 100%;
`;
const SegmentsWrapper = styled(Horizontal)`
  gap: 1px;
  flex: 1;

  & > * {
    flex: 1;
  }
`;
const Segment = styled.div`
  height: var(--unit);
  border-radius: 2px;
  background-color: var(--c-nd-150);
  position: relative;
  isolation: isolate;

  &.full {
    background-color: var(--c-yellow-600);
    box-shadow: 0px 0px 10px 0px rgba(255, 255, 255, 0.65) inset;
    filter: drop-shadow(0px 1px 7px var(--c-yellow-600));
  }

  &.partial {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: calc(var(--progress) * 100%);
      background-color: var(--c-yellow-600);
    }
  }
`;
const ShinyText = styled.span`
  color: var(--c-yellow-200);
  text-shadow: 0px 1px 7px var(--c-yellow-600);
`;
const ArrowWrapper = styled(Horizontal).attrs({ center: true })`
  color: var(--c-yellow-200);
`;

type Props = {
  cardCollection: SplitProgressBar_cardCollection;
  userCardCollection: SplitProgressBar_userCardCollection | null;
};
export const SplitProgressBar = ({
  cardCollection,
  userCardCollection,
}: Props) => {
  const { bonusThresholds } = cardCollection;
  const { score = 0, bonus = 0 } = userCardCollection || {};

  if (!bonusThresholds.length) {
    return null;
  }

  return (
    <Root>
      <SegmentsWrapper>
        {bonusThresholds.map((bonusThreshold, index) => {
          const fullBar = score >= bonusThreshold.threshold;
          const previousBonusThreshold = bonusThresholds[index - 1] as
            | SplitProgressBar_cardCollection['bonusThresholds'][number]
            | undefined;
          const previousBonusThresholdValue =
            previousBonusThreshold?.threshold || 0;
          const partialBar = !fullBar && score >= previousBonusThresholdValue;
          const partialProgress = partialBar
            ? (score - previousBonusThresholdValue) /
              (bonusThreshold.threshold - previousBonusThresholdValue)
            : undefined;

          return (
            <Segment
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={classNames({
                full: fullBar,
                partial: partialBar,
              })}
              style={{ '--progress': partialProgress } as CSSProperties}
            />
          );
        })}
      </SegmentsWrapper>
      <Horizontal gap={0.5}>
        <ArrowWrapper>
          <CollectionArrow width={12} height={12} shiny />
        </ArrowWrapper>
        <ShinyText>
          <LabelM bold as="span">
            +{bonus * 100}
          </LabelM>
          <LabelS as="span">%</LabelS>
        </ShinyText>
      </Horizontal>
    </Root>
  );
};

SplitProgressBar.fragments = {
  cardCollection: gql`
    fragment SplitProgressBar_cardCollection on CardCollection {
      slug
      bonusThresholds {
        threshold
      }
    }
  ` as TypedDocumentNode<SplitProgressBar_cardCollection>,
  userCardCollection: gql`
    fragment SplitProgressBar_userCardCollection on UserCardCollection {
      id
      score
      bonus
    }
  ` as TypedDocumentNode<SplitProgressBar_userCardCollection>,
};
